'use client';
import qs from 'qs';
import { useEffect, useState } from 'react';

import Skeleton from '../../components/Skeleton';
import TileData from '../../components/TileData';
import { fetchUser } from '../../lib/user';

import { Checkbox } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { useRouter } from '@lib/navigation';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { IPRESSO_SOURCES } from '@utils/api/client/ipresso';
import {
  IpressoFetchResponse,
  IpressoPostBody,
} from '@utils/api/server/ipresso';
import { newslettersByCountries } from '@utils/newsletter';
import { useUserStore } from '@utils/user';
import { User } from 'types/user';

interface Props {
  labels: {
    title: string;
    toast: {
      success: string;
      error: string;
      atLeastOne: string;
    };
    newsletters: {
      [key: string]: {
        label: string;
      };
    };
  };
  locale: string;
}

export default function Tile({ labels, locale }: Props) {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [newsletterChecked, setNewsletterChecked] = useState<any[]>([]);
  const userStore = useUserStore();
  const router = useRouter();

  useEffect(() => {
    if (!userStore.isReady || !userStore.isLoggedIn || !userStore.accessToken) {
      return;
    }

    fetchUser(setUser, userStore.accessToken);
  }, [userStore.accessToken]);

  const prepareInitialCategories = async () => {
    if (!user) return [];

    const params = {
      email: user.email,
      lang: locale,
    };
    const query = qs.stringify(params);
    try {
      const res = await fetchApi<IpressoFetchResponse>(
        `/campaign/fetch?${query}`,
        {
          method: 'GET',
          noAuth: true,
        },
      );

      const resData = res.data.data;

      if (resData === undefined) {
        return [];
      }

      return Object.keys(resData?.categoryData);
    } catch (e) {
      // eslint-disable-next-line no-console
      logger.error(e);
      return [];
    }
  };

  useEffect(() => {
    if (!user) return;
    const fetchCategories = async () => {
      prepareInitialCategories().then((res) => {
        if (newsletterChecked.length === 0) {
          setNewsletterChecked(
            res.filter((element) => ![undefined, 'total'].includes(element)),
          );
        }
      });
    };
    if (user && user.email) {
      fetchCategories();
      return;
    }
  }, [user]);

  if (!Object.keys(newslettersByCountries).includes(locale)) {
    router.push('/profile/edit');
    return <Skeleton />;
  }

  if (!userStore.isReady || !user) {
    return <Skeleton />;
  }

  return (
    <TileData className="col-span-2" title={labels.title}>
      {newslettersByCountries[locale].map((newsletter) => {
        const itemValue = newsletterChecked.includes(newsletter);

        return (
          <div key={newsletter} className="checkbox-grid mb-2">
            <Checkbox
              defaultChecked={itemValue}
              checked={itemValue}
              disabled={isLoading}
              label={labels.newsletters[newsletter].label}
              onChange={async () => {
                if (!user) return;

                // At least one element on the list has to be selected.
                if ((itemValue === true) && (newsletterChecked.length === 1)) {
                  toastError(labels.toast.atLeastOne);
                  return;
                }

                setLoading(true);

                try {
                  const bodyObject: IpressoPostBody = {
                    contactData: {
                      email: user?.email,
                    },
                    lang: {
                      category: locale,
                      agreement: locale,
                    },
                    categoryData: {
                      [newsletter]: !itemValue,
                    },
                    agreementData: {
                      newsletter: true,
                    },
                    source: IPRESSO_SOURCES.NEWSLETTER,
                  };

                  await fetchApi<IpressoFetchResponse>('/campaign/edit', {
                    method: 'POST',
                    noAuth: true,
                    body: JSON.stringify(bodyObject),
                  });

                  await setNewsletterChecked(
                    itemValue
                      ? newsletterChecked.filter((word) => word !== newsletter)
                      : [...newsletterChecked, newsletter],
                  );

                  toastSuccess(labels.toast.success);
                } catch (e) {
                  // eslint-disable-next-line no-console
                  logger.error(e, 'Newsletter update %s', (e as any)?.message);
                  toastError(labels.toast.error);
                } finally {
                  setLoading(false);
                }
              }}
            />
          </div>
        );
      })}
    </TileData>
  );
}
